import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { getFullImageUrl as getFullImageUrlUtil } from '../../../utils/imageUtils';
import './MediaGallery.css';

// Maximum image file size (10 MB) - must match backend
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const MediaGallery = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    category: 'general',
    description: '',
    restaurant: '',
    store: ''
  });

  useEffect(() => {
    fetchMedia();
  }, [categoryFilter]);

  useEffect(() => {
    // Fetch restaurants for restaurant owners and users
    if (user?.role === 'restaurant_owner' || user?.role === 'restaurant_user') {
      fetchRestaurants();
    }
    // Fetch stores for store owners and users
    if (user?.role === 'store_owner' || user?.role === 'store_user') {
      fetchStores();
    }
  }, [user]);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/restaurant-owner/restaurants`);
      setRestaurants(response.data);
      // Auto-select first restaurant if only one exists
      if (response.data.length === 1) {
        setUploadForm(prev => ({ ...prev, restaurant: response.data[0]._id }));
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/store-owner/stores`);
      setStores(response.data);
      // Auto-select first store if only one exists
      if (response.data.length === 1) {
        setUploadForm(prev => ({ ...prev, store: response.data[0]._id }));
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const url = categoryFilter === 'all' 
        ? `${API_URL}/media`
        : `${API_URL}/media?category=${categoryFilter}`;
      const response = await axios.get(url);
      setMedia(response.data);
    } catch (error) {
      console.error('Error fetching media:', error);
      showError(t('admin.errorLoadingMedia'));
    } finally {
      setLoading(false);
    }
  };

  const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
  const hasImageExtension = (name) => ALLOWED_IMAGE_EXTENSIONS.some(ext => (name || '').toLowerCase().endsWith(ext));

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImageByType = file.type && file.type.startsWith('image/');
    const isImageByExt = hasImageExtension(file.name);
    if (!isImageByType && !isImageByExt) {
      showError(t('admin.onlyImagesAllowed'));
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      showError(t('admin.fileTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      
      // Add restaurant or store ID if selected
      if (uploadForm.restaurant) {
        formData.append('restaurant', uploadForm.restaurant);
      }
      if (uploadForm.store) {
        formData.append('store', uploadForm.store);
      }

      const token = localStorage.getItem('token');
      const headers = { ...(token && { Authorization: `Bearer ${token}` }) };
      // Do not set Content-Type: browser must set multipart/form-data with boundary for FormData
      const response = await axios.post(`${API_URL}/media/upload`, formData, {
        headers,
        maxContentLength: MAX_IMAGE_SIZE_BYTES,
        maxBodyLength: MAX_IMAGE_SIZE_BYTES
      });

      setMedia([response.data, ...media]);
      setUploadForm(prev => ({ 
        category: 'general', 
        description: '',
        restaurant: prev.restaurant, // Keep restaurant/store selection
        store: prev.store
      }));
      e.target.value = ''; // Reset file input
      showSuccess(t('admin.mediaUploaded'));
    } catch (error) {
      console.error('Error uploading media:', error);
      showError(t('admin.errorUploadingMedia') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm(t('admin.confirmDeleteMedia'))) return;
    
    try {
      await axios.delete(`${API_URL}/media/${mediaId}`);
      setMedia(media.filter(m => m._id !== mediaId));
      showSuccess(t('admin.mediaDeleted'));
    } catch (error) {
      console.error('Error deleting media:', error);
      showError(t('admin.errorDeletingMedia'));
    }
  };

  // Use the shared utility function for consistency
  const getFullImageUrl = (url) => {
    return getFullImageUrlUtil(url);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="media-gallery-container">
      <div className="media-gallery-header">
        <h2>{t('admin.mediaGallery')}</h2>
        <div className="upload-section">
          <div className="upload-controls">
            {/* Restaurant selection for restaurant owners */}
            {(user?.role === 'restaurant_owner' || user?.role === 'restaurant_user') && restaurants.length > 0 && (
              <select
                value={uploadForm.restaurant}
                onChange={(e) => setUploadForm({ ...uploadForm, restaurant: e.target.value, store: '' })}
                className="restaurant-select"
                required
              >
                <option value="">{t('admin.selectRestaurant')}...</option>
                {restaurants.map(restaurant => (
                  <option key={restaurant._id} value={restaurant._id}>
                    {restaurant.name}
                  </option>
                ))}
              </select>
            )}
            {/* Store selection for store owners */}
            {(user?.role === 'store_owner' || user?.role === 'store_user') && stores.length > 0 && (
              <select
                value={uploadForm.store}
                onChange={(e) => setUploadForm({ ...uploadForm, store: e.target.value, restaurant: '' })}
                className="store-select"
                required
              >
                <option value="">{t('admin.selectStore')}...</option>
                {stores.map(store => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={uploadForm.category}
              onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
              className="category-select"
            >
              <option value="general">{t('admin.general')}</option>
              <option value="banner">{t('admin.banner')}</option>
              <option value="menu">{t('admin.menu')}</option>
              <option value="product">{t('admin.product')}</option>
            </select>
            <input
              type="text"
              placeholder={t('admin.description')}
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              className="description-input"
            />
            <label className="upload-btn">
              {uploading ? t('admin.uploading') : t('admin.uploadImage')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
            <span className="upload-max-size-hint">
              {t('admin.maxImageSize') || 'Max 10 MB'}
            </span>
          </div>
        </div>
      </div>

      <div className="media-filters">
        <label>{t('admin.filterByCategory')}:</label>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">{t('admin.all')}</option>
          <option value="general">{t('admin.general')}</option>
          <option value="banner">{t('admin.banner')}</option>
          <option value="menu">{t('admin.menu')}</option>
          <option value="product">{t('admin.product')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">{t('admin.loading')}</div>
      ) : (
        <div className="media-grid">
          {media.length === 0 ? (
            <div className="no-media">{t('admin.noMediaFound')}</div>
          ) : (
            media.map(item => (
              <div key={item._id} className="media-item">
                <div className="media-image-container">
                  <img
                    src={getFullImageUrl(item.url)}
                    alt={item.originalName}
                    onClick={() => setSelectedMedia(item)}
                    className="media-image"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="media-overlay">
                    <button
                      className="delete-media-btn"
                      onClick={() => handleDelete(item._id)}
                      title={t('admin.delete')}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="media-info">
                  <div className="media-name" title={item.originalName}>
                    {item.originalName}
                  </div>
                  <div className="media-meta">
                    <span className="media-category">{item.category}</span>
                    <span className="media-size">{formatFileSize(item.size)}</span>
                  </div>
                  {item.restaurant && (
                    <div className="media-restaurant" style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                      Restaurant: {item.restaurant.name || item.restaurant}
                    </div>
                  )}
                  {item.store && (
                    <div className="media-store" style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                      Store: {item.store.name || item.store}
                    </div>
                  )}
                  {item.description && (
                    <div className="media-description">{item.description}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {selectedMedia && (
        <div className="media-preview-overlay" onClick={() => setSelectedMedia(null)}>
          <div className="media-preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-preview" onClick={() => setSelectedMedia(null)}>×</button>
            <img src={getFullImageUrl(selectedMedia.url)} alt={selectedMedia.originalName} />
            <div className="media-preview-info">
              <h3>{selectedMedia.originalName}</h3>
              <p><strong>{t('admin.category')}:</strong> {selectedMedia.category}</p>
              <p><strong>{t('admin.size')}:</strong> {formatFileSize(selectedMedia.size)}</p>
              <p><strong>{t('admin.url')}:</strong> <code>{getFullImageUrl(selectedMedia.url)}</code></p>
              {selectedMedia.description && (
                <p><strong>{t('admin.description')}:</strong> {selectedMedia.description}</p>
              )}
              <button
                className="copy-url-btn"
                onClick={() => {
                  navigator.clipboard.writeText(getFullImageUrl(selectedMedia.url));
                  showSuccess(t('admin.urlCopied'));
                }}
              >
                {t('admin.copyUrl')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
